import { CloseOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateTimeslotMutation, useUpdateTimeslotMutation } from "api/mutations/timeslots";
import KEYS from "api/queries/keys";
import { useSeminarsAllSessionsQuery } from "api/queries/seminars";
import { useTimeslotsQuery } from "api/queries/timeslots";
import DateRenderer from "components/DateRenderer";
import FullHeightForm from "components/FullHeightForm";
import { Button, Drawer, Form, Input, Modal, Select, Space, Table, Typography } from "components/styleguide";
import DatePickerAvailableDaysWrapper from "components/styleguide/components/DatePickerAvailableDaysWrapper";
import {
  ForumSeminarSessionsModel,
  TimeSlotResponseModel,
  TimeSlotSessionResponseModel,
  TimeSlotType,
} from "generated/api";
import { EventLayoutContext } from "layouts/EventLayout/types";
import moment, { Moment } from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { toWallClockMoment } from "utils/dateUtils";

import {
  getDuplicateTimeSlotValidationMessage,
  getExistingDuplicateTimeSlotSessionIds,
  handleTimeslotError,
  isDuplicateSessionError,
  isDuplicateTimeSlotError,
  normalizeSessionOrder,
  showTimeslotValidationError,
} from "../../../Timeslots/helpers";
import useUnsavedChangesPrompt from "../../../Timeslots/useUnsavedChangesPrompt";

export type CreatedTimeslotSession = {
  id: number;
  order?: number;
  startTime?: string;
  endTime?: string;
  timeSlotId: number;
  timeSlotDescription: string;
};

interface CreateTimeslotSessionDrawerProps {
  facilityCode?: string;
  forumId: number;
  open: boolean;
  onClose: () => void;
  onCreated: (session: CreatedTimeslotSession) => Promise<boolean>;
}

type DrawerFormValues = {
  duration?: [Moment, Moment];
  newTimeslotDescription?: string;
  timeslotId?: number;
};

const CreateTimeslotSessionDrawer: FC<CreateTimeslotSessionDrawerProps> = ({
  facilityCode,
  forumId,
  open,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { forum } = useOutletContext<EventLayoutContext>();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<DrawerFormValues>();
  const [conferenceTimeslots, setConferenceTimeslots] = useState<TimeSlotResponseModel[]>([]);
  const [selectedTimeslotId, setSelectedTimeslotId] = useState<number | undefined>();
  const [selectedTimeslotSessions, setSelectedTimeslotSessions] = useState<TimeSlotSessionResponseModel[]>([]);
  const [isCreatingTimeslot, setIsCreatingTimeslot] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isCompletingSave, setIsCompletingSave] = useState(false);
  const [pendingCreatedSession, setPendingCreatedSession] = useState<CreatedTimeslotSession>();
  const [hasLoadedTimeslots, setHasLoadedTimeslots] = useState(false);

  const createTimeslotMutation = useCreateTimeslotMutation();
  const updateTimeslotMutation = useUpdateTimeslotMutation();
  const isSaving = createTimeslotMutation.isLoading || updateTimeslotMutation.isLoading || isCompletingSave;
  const activeTimeslotId = isCreatingTimeslot ? undefined : selectedTimeslotId;

  useEffect(() => {
    if (!open) {
      return;
    }

    form.resetFields();
    setConferenceTimeslots([]);
    setSelectedTimeslotId(undefined);
    setSelectedTimeslotSessions([]);
    setIsCreatingTimeslot(false);
    setIsDirty(false);
    setPendingCreatedSession(undefined);
    setHasLoadedTimeslots(false);
  }, [form, open]);

  const timeslotsQuery = useTimeslotsQuery(
    { forumId },
    {
      enabled: open,
      onSuccess: ({ data }: { data: TimeSlotResponseModel[] }) => {
        const timeslots = (data ?? []).filter((timeslot) => timeslot.timeSlotType === TimeSlotType.Conference);
        setConferenceTimeslots(timeslots);
        setHasLoadedTimeslots(true);
        setIsCreatingTimeslot((currentValue) => (timeslots.length === 0 ? true : currentValue));
        if (selectedTimeslotId) {
          setSelectedTimeslotSessions(timeslots.find((timeslot) => timeslot.id === selectedTimeslotId)?.sessions ?? []);
        }
      },
      onError: () => toast.error(t("Something went wrong")),
    },
  );
  const facilitySeminarSessionsQuery = useSeminarsAllSessionsQuery({ forumId, facilityCode }, { enabled: false });

  const noConferenceTimeslots = hasLoadedTimeslots && conferenceTimeslots.length === 0;

  useEffect(() => {
    if (!activeTimeslotId) {
      setSelectedTimeslotSessions([]);
    }
  }, [activeTimeslotId]);

  const isQuerying = timeslotsQuery.isFetching;
  const isSaveDisabled = isSaving || isQuerying;

  useUnsavedChangesPrompt(
    isDirty,
    isSaving,
    t("Unsaved timeslot session changes will be lost if you leave this drawer."),
  );

  const confirmDiscard = (content: string, onOk: () => void) => {
    Modal.confirm({
      title: t("confirmation"),
      content,
      okText: t("yes"),
      cancelText: t("no"),
      onOk,
    });
  };

  const requestClose = () => {
    if (isSaving) {
      return;
    }

    const noTimeslotsWarning = noConferenceTimeslots && isCreatingTimeslot;

    if (isDirty || noTimeslotsWarning) {
      confirmDiscard(
        noTimeslotsWarning
          ? t("No existing conference timeslots exist. Do you want to exit without saving your changes?")
          : t("Unsaved session or timeslot changes will be lost. Do you want to exit?"),
        onClose,
      );
      return;
    }

    onClose();
  };

  const selectExistingTimeslot = (timeslotId: number) => {
    setSelectedTimeslotId(timeslotId);
    setSelectedTimeslotSessions(conferenceTimeslots.find((timeslot) => timeslot.id === timeslotId)?.sessions ?? []);
    form.setFieldValue("timeslotId", timeslotId);
    setIsDirty(true);
  };

  const startNewTimeslot = () => {
    setSelectedTimeslotId(undefined);
    setSelectedTimeslotSessions([]);
    form.setFieldValue("timeslotId", undefined);
    setIsCreatingTimeslot(true);
    setIsDirty(true);
  };

  const closeNewTimeslot = () => {
    if (noConferenceTimeslots) {
      confirmDiscard(
        t("No existing conference timeslots exist. Do you want to exit without saving your changes?"),
        onClose,
      );
      return;
    }

    form.resetFields(["newTimeslotDescription"]);
    setIsCreatingTimeslot(false);
    setIsDirty(true);
  };

  const disabledDate = (value: Moment) => {
    if (!forum?.startDate || !forum?.endDate) {
      return false;
    }

    const day = toWallClockMoment(value).startOf("day");
    const forumStartDay = toWallClockMoment(forum.startDate).startOf("day");
    const forumEndDay = toWallClockMoment(forum.endDate).endOf("day");

    return day.isBefore(forumStartDay) || day.isAfter(forumEndDay);
  };

  const handleSaveError = (error: unknown) => {
    const response = (error as { response?: unknown }).response;

    if (isDuplicateTimeSlotError(response)) {
      const message = getDuplicateTimeSlotValidationMessage(t);
      form.setFields([{ name: "newTimeslotDescription", errors: [message] }]);
    } else if (isDuplicateSessionError(response)) {
      const message = getExistingDuplicateTimeSlotSessionIds(response).length
        ? String(t("Session time already exists"))
        : String(t("Duplicate session time"));
      form.setFields([{ name: "duration", errors: [message] }]);
    }

    handleTimeslotError(response, t);
  };

  const confirmFacilityOverlap = () =>
    new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: t("warning"),
        content: t(
          "Warning: The newly created timeslot session will be unavailable for {{facilityCode}} due to an overlap with existing seminar sessions. Would you like to continue?",
          { facilityCode },
        ),
        okText: t("Continue"),
        cancelText: t("Cancel"),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const confirmSaveIfFacilityIsUnavailable = async (start: Moment, end: Moment) => {
    if (!facilityCode) {
      return true;
    }

    const result = await facilitySeminarSessionsQuery.refetch({ throwOnError: true });
    const facilitySessions = result.data?.data?.items ?? [];
    const hasOverlap = facilitySessions.some((session: ForumSeminarSessionsModel) => {
      if (session.facilityCode !== facilityCode || !session.startDate || !session.endDate) {
        return false;
      }

      const existingStart = toWallClockMoment(session.startDate);
      const existingEnd = toWallClockMoment(session.endDate);

      return existingStart.isBefore(end) && existingEnd.isAfter(start);
    });

    return !hasOverlap || confirmFacilityOverlap();
  };

  const finishSave = async (createdSession: CreatedTimeslotSession) => {
    await Promise.all([
      queryClient.invalidateQueries([KEYS.GET_TIMESLOTS]),
      queryClient.invalidateQueries([KEYS.GET_TIMESLOT]),
      queryClient.invalidateQueries([KEYS.GET_TIMESLOT_SESSIONS]),
      queryClient.invalidateQueries([KEYS.GET_ALL_TIMESLOTS_SESSIONS]),
      queryClient.invalidateQueries([KEYS.GET_FORUM_VENUE_AVAILABLE_TIMESLOTS]),
    ]);

    const wasSelected = await onCreated(createdSession);
    setPendingCreatedSession(undefined);
    setIsDirty(false);
    toast.success(t(wasSelected ? "Timeslot session created and selected" : "Timeslot session created"));
  };

  const submit = async (values: DrawerFormValues) => {
    const [start, end] = values.duration ?? [];

    if (!start || !end) {
      return;
    }

    const startTime = moment(start).format("YYYY-MM-DDTHH:mm:ss");
    const endTime = moment(end).format("YYYY-MM-DDTHH:mm:ss");

    let sessionToFinish = pendingCreatedSession;
    setIsCompletingSave(true);
    try {
      if (!sessionToFinish) {
        const shouldContinue = await confirmSaveIfFacilityIsUnavailable(start, end);
        if (!shouldContinue) {
          return;
        }

        let savedTimeslot: TimeSlotResponseModel;
        let existingSessionIds = new Set<number>();

        if (isCreatingTimeslot) {
          const description = values.newTimeslotDescription?.trim() ?? "";
          const response = await createTimeslotMutation.mutateAsync({
            forumId,
            timeSlotRequestModel: {
              timeSlotType: TimeSlotType.Conference,
              description,
              sessions: [{ startTime, endTime, order: 1 }],
            },
          });
          savedTimeslot = response.data;
        } else {
          const timeslot = conferenceTimeslots.find((item) => item.id === selectedTimeslotId);

          if (!timeslot?.id) {
            form.setFields([{ name: "timeslotId", errors: [t("Please select a conference timeslot")] }]);
            toast.error(t("The selected timeslot could not be loaded"));
            return;
          }

          const existingSessions = normalizeSessionOrder(timeslot.sessions ?? []);
          existingSessionIds = new Set(existingSessions.map((session) => session.id).filter(Boolean) as number[]);
          const response = await updateTimeslotMutation.mutateAsync({
            forumId,
            timeSlotId: timeslot.id,
            timeSlotUpdateRequestModel: {
              description: timeslot.description ?? "",
              timeSlotType: timeslot.timeSlotType ?? TimeSlotType.Conference,
              disableOrdering: timeslot.disableOrdering ?? false,
              sessions: [
                ...existingSessions.map((session) => ({
                  id: session.id,
                  startTime: session.startTime,
                  endTime: session.endTime,
                  order: session.order,
                })),
                {
                  id: null,
                  startTime,
                  endTime,
                  order: existingSessions.length + 1,
                },
              ],
            },
          });
          savedTimeslot = response.data;
        }

        const savedSessions = normalizeSessionOrder(savedTimeslot.sessions ?? []);
        const createdSession =
          savedSessions.find((session) => !!session.id && !existingSessionIds.has(session.id)) ??
          savedSessions[savedSessions.length - 1];

        if (!createdSession?.id || !savedTimeslot.id || !savedTimeslot.description) {
          throw new Error("The created timeslot session was not returned by the server");
        }

        sessionToFinish = {
          id: createdSession.id,
          order: createdSession.order,
          startTime: createdSession.startTime,
          endTime: createdSession.endTime,
          timeSlotId: savedTimeslot.id,
          timeSlotDescription: savedTimeslot.description,
        };
        setPendingCreatedSession(sessionToFinish);
      }

      await finishSave(sessionToFinish);
    } catch (error: unknown) {
      if (sessionToFinish) {
        toast.error(t("The timeslot session was saved, but the items could not be refreshed. Please try again."));
      } else {
        handleSaveError(error);
      }
    } finally {
      setIsCompletingSave(false);
    }
  };

  const sessionColumns = [
    {
      title: t("Start Time"),
      dataIndex: "startTime",
      render: (startTime: string) => <DateRenderer date={startTime} showTime wallClock />,
    },
    {
      title: t("End Time"),
      dataIndex: "endTime",
      render: (endTime: string) => <DateRenderer date={endTime} showTime wallClock />,
    },
    {
      title: t("Seminars added"),
      key: "seminarSessionsCount",
      render: (_: unknown, session: TimeSlotSessionResponseModel) => session.dependencies?.seminarSessionsCount ?? 0,
    },
    {
      title: t("Itineraries added"),
      key: "itineraryItemsCount",
      render: (_: unknown, session: TimeSlotSessionResponseModel) => session.dependencies?.itineraryItemsCount ?? 0,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={requestClose}
      placement="right"
      title={t("Create New Timeslot Session")}
      size="large"
      closable={!isSaving}
      keyboard={!isSaving}
      maskClosable={!isSaving}
      destroyOnClose
    >
      <FullHeightForm<DrawerFormValues>
        layout="vertical"
        form={form}
        onFinish={submit}
        onFinishFailed={() => showTimeslotValidationError(t)}
        onValuesChange={() => setIsDirty(true)}
        actionsPrepend={
          <Button onClick={requestClose} disabled={isSaving}>
            {t("Cancel")}
          </Button>
        }
        actionsAppend={
          <Button htmlType="submit" type="primary" loading={isSaving} disabled={isSaveDisabled}>
            {t("Save")}
          </Button>
        }
      >
        <Form.Item
          name="timeslotId"
          label={t("Select or add Timeslot")}
          required
          rules={[{ required: !isCreatingTimeslot && !noConferenceTimeslots }]}
          extra={
            !isCreatingTimeslot && !noConferenceTimeslots ? (
              <Button onClick={startNewTimeslot} style={{ marginTop: 8 }} disabled={isSaving}>
                {t("Add New Timeslot")}
              </Button>
            ) : undefined
          }
        >
          <Select
            disabled={isSaving || isCreatingTimeslot || noConferenceTimeslots}
            loading={timeslotsQuery.isFetching}
            placeholder={
              isCreatingTimeslot
                ? t("Creating a new timeslot")
                : noConferenceTimeslots
                ? t("No conference timeslots available")
                : t("Select a conference timeslot")
            }
            onChange={selectExistingTimeslot}
          >
            {conferenceTimeslots.map((timeslot) => (
              <Select.Option key={timeslot.id} value={timeslot.id}>
                <Space style={{ display: "flex", justifyContent: "space-between", minWidth: 300 }}>
                  <span>{timeslot.description}</span>
                  <Typography.Text type="secondary">
                    {t("{{count}} session(s)", { count: timeslot.sessions?.length ?? 0 })}
                  </Typography.Text>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {isCreatingTimeslot && (
          <div style={{ border: "1px solid #d9d9d9", borderRadius: 4, marginBottom: 16, padding: 16 }}>
            <Space style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <Typography.Text strong>{t("Add New Timeslot")}</Typography.Text>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={closeNewTimeslot}
                aria-label={t("Close")}
                disabled={isSaving}
              />
            </Space>
            <Form.Item label={t("Time Type")} style={{ marginBottom: 16 }}>
              <Input value={t("Conference")} disabled />
            </Form.Item>
            <Form.Item
              name="newTimeslotDescription"
              label={t("Description")}
              style={{ marginBottom: 0 }}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("Please enter a timeslot description"),
                },
              ]}
            >
              <Input disabled={isSaving} />
            </Form.Item>
          </div>
        )}

        <Typography.Title level={5}>{t("Add Session")}</Typography.Title>
        <Form.Item
          name="duration"
          label={t("Start and End Time")}
          rules={[
            {
              validator: async (_, duration?: [Moment, Moment]) => {
                if (!duration?.[0] || !duration?.[1]) {
                  throw new Error(t("Start time and end time are required"));
                }

                if (!moment(duration[0]).isBefore(moment(duration[1]))) {
                  throw new Error(t("End time must be later than start time"));
                }

                if (
                  (forum?.startDate && moment(duration[0]).isBefore(toWallClockMoment(forum.startDate))) ||
                  (forum?.endDate && moment(duration[1]).isAfter(toWallClockMoment(forum.endDate)))
                ) {
                  throw new Error(t("Session start and end time must be within the forum dates"));
                }
              },
            },
          ]}
        >
          <DatePickerAvailableDaysWrapper.RangePickerAvailableDaysWrapper
            style={{ width: "100%" }}
            showTime
            minuteStep={5}
            placeholder={[t("Start Time"), t("End Time")]}
            availableDatesGetterType="ActualDays"
            eventStartDate={toWallClockMoment(forum?.startDate)}
            eventEndDate={toWallClockMoment(forum?.endDate)}
            disabled={isSaving || (!activeTimeslotId && !isCreatingTimeslot)}
            disabledDate={disabledDate}
          />
        </Form.Item>

        <Typography.Title level={5}>{t("Existing Timeslot Sessions")}</Typography.Title>
        {activeTimeslotId && selectedTimeslotSessions.length > 0 ? (
          <Table
            dataSource={selectedTimeslotSessions}
            columns={sessionColumns}
            pagination={false}
            rowKey="id"
            size="small"
          />
        ) : (
          <Typography.Text>{t("No existing sessions")}</Typography.Text>
        )}
      </FullHeightForm>
    </Drawer>
  );
};

export default CreateTimeslotSessionDrawer;
